/* ==========================================================================
   Vildblad 2.2 — gillade produkter, flikar för kategorier, presentkort, nedsatt pris
   Ingen extern kod. Listan med gillade produkter sparas i kundens webbläsare.
   ========================================================================== */
(() => {
  'use strict';

  const V = window.Vildblad || (window.Vildblad = {});
  const S = V.strings || {};
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = (V.routes && V.routes.root) || '/';
  const rootUrl = root.endsWith('/') ? root : `${root}/`;
  const fill = (str, map) => String(str || '').replace(/__(\w+)__/g, (m, k) => (k in map ? map[k] : m));
  const say = (text) => { if (typeof V.toast === 'function') V.toast(text); };

  /* ==========================================================================
     Gillade produkter
     ========================================================================== */
  const Wishlist = (() => {
    const KEY = 'vildblad:wishlist';
    const MAX = 60;
    const HANDLE = /^[^\s/?#"'<>]+$/;
    let memory = [];

    const read = () => {
      try {
        const raw = window.localStorage.getItem(KEY);
        if (!raw) return memory.slice();
        const list = JSON.parse(raw);
        return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && HANDLE.test(h)) : [];
      } catch (e) {
        return memory.slice();
      }
    };

    const write = (list) => {
      memory = list.slice(0, MAX);
      try { window.localStorage.setItem(KEY, JSON.stringify(memory)); } catch (e) { /* privat läge: bara i minnet */ }
    };

    const has = (handle) => read().includes(handle);

    // Etiketten är alltid "Gilla <produkt>" – aria-pressed säger om den är gillad
    function label(btn, on) {
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    function countText(n) {
      if (n === 1) return S.wishCountOne || '1';
      return fill(S.wishCountOther, { count: n });
    }

    function sync(scope = document) {
      const list = read();
      $$('[data-wish]', scope).forEach((btn) => label(btn, list.includes(btn.dataset.wish)));
      if (scope !== document) return;
      $$('[data-wish-count]').forEach((el) => {
        const prev = parseInt(el.dataset.count, 10) || 0;
        el.dataset.count = String(list.length);
        el.textContent = list.length > 0 ? String(list.length) : '';
        if (list.length > prev && !reduceMotion) {
          el.classList.remove('is-bumping');
          void el.offsetWidth;
          el.classList.add('is-bumping');
        }
      });
      $$('[data-wish-count-text]').forEach((el) => { el.textContent = list.length ? `(${countText(list.length)})` : ''; });
      $$('[data-wish-link]').forEach((el) => {
        el.classList.toggle('has-items', list.length > 0);
        el.setAttribute('aria-label', list.length ? `${S.wishTitle || ''} (${countText(list.length)})` : (S.wishTitle || ''));
      });
    }

    function toggle(handle, title) {
      let list = read();
      const on = !list.includes(handle);
      list = on ? [handle, ...list.filter((h) => h !== handle)] : list.filter((h) => h !== handle);
      write(list);
      sync();
      say(on ? S.wishAdded : S.wishRemoved);
      document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handle, on, title, list: read() } }));
      return on;
    }

    // Klick på ett hjärta, var det än finns (även kort som laddas in senare)
    document.addEventListener('click', (e) => {
      const btn = e.target.closest && e.target.closest('[data-wish]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const on = toggle(btn.dataset.wish, btn.dataset.wishTitle);
      if (on && !reduceMotion) {
        btn.classList.remove('is-popping');
        void btn.offsetWidth;
        btn.classList.add('is-popping');
      }
    });

    // Andra flikar i samma webbläsare
    window.addEventListener('storage', (e) => { if (e.key === KEY) sync(); });
    // Tillbaka med bakåtknappen (sidan kan komma från bfcache)
    window.addEventListener('pageshow', (e) => { if (e.persisted) sync(); });

    // Nya produktkort (förslag, sökning, varukorg) får rätt läge direkt
    if ('MutationObserver' in window) {
      let pending = null;
      new MutationObserver((records) => {
        if (!records.some((r) => Array.from(r.addedNodes).some((n) => n.nodeType === 1 && (n.matches('[data-wish]') || n.querySelector('[data-wish]'))))) return;
        clearTimeout(pending);
        pending = setTimeout(() => sync(), 30);
      }).observe(document.body, { childList: true, subtree: true });
    }

    return { read, write, has, toggle, sync, countText };
  })();

  /* ---------- Sidan Gillade produkter ---------- */
  function initWishlistPage(section) {
    const grid = $('[data-wishlist-grid]', section);
    const empty = $('[data-wishlist-empty]', section);
    const loading = $('[data-wishlist-loading]', section);
    const count = $('[data-wishlist-count]', section);
    const note = $('[data-wishlist-note]', section);
    if (!grid) return;

    const setCount = (n) => { if (count) count.textContent = n ? Wishlist.countText(n) : ''; };
    const showEmpty = () => {
      grid.hidden = true;
      if (loading) loading.hidden = true;
      if (empty) empty.hidden = false;
      if (note) note.hidden = true;
      setCount(0);
    };

    async function load() {
      const list = Wishlist.read();
      if (!list.length) { showEmpty(); return; }
      if (loading) loading.hidden = false;
      // Högst sex hämtningar åt gången, så att butiken inte får alla på en gång
      const getCard = (handle) => fetch(`${rootUrl}products/${encodeURIComponent(handle)}?view=card`, { credentials: 'same-origin' })
        .then((res) => {
          if (res.status === 404) return null; // produkten finns inte längre
          if (!res.ok) return undefined;
          return res.text();
        })
        .catch(() => undefined);
      const results = new Array(list.length);
      let next = 0;
      const worker = async () => {
        while (next < list.length) {
          const i = next++;
          results[i] = await getCard(list[i]);
        }
      };
      await Promise.all(Array.from({ length: Math.min(6, list.length) }, worker));

      // Ta bort produkter som inte finns kvar. Nätverksfel behåller produkten till nästa gång.
      const gone = list.filter((h, i) => results[i] === null);
      if (gone.length) Wishlist.write(Wishlist.read().filter((h) => !gone.includes(h)));

      const items = [];
      list.forEach((handle, i) => {
        const html = results[i];
        if (typeof html !== 'string' || html.indexOf('data-product-card') === -1) return;
        const li = document.createElement('li');
        li.dataset.wishItem = handle;
        li.innerHTML = html;
        items.push(li);
      });

      if (loading) loading.hidden = true;
      if (!items.length) {
        if (results.some((r) => r === undefined)) {
          grid.hidden = true;
          if (count) count.textContent = S.wishError || '';
        } else {
          showEmpty();
        }
        Wishlist.sync();
        return;
      }
      grid.replaceChildren(...items);
      grid.hidden = false;
      if (empty) empty.hidden = true;
      if (note) note.hidden = false;
      setCount(items.length);
      Wishlist.sync();
    }

    // Tar kunden bort ett hjärta på sidan försvinner kortet
    document.addEventListener('wishlist:change', (e) => {
      const { handle, on } = e.detail || {};
      if (on) return;
      const li = $(`[data-wish-item="${CSS.escape(handle)}"]`, grid);
      if (!li) return;
      const done = () => {
        li.remove();
        const left = $$('[data-wish-item]', grid).length;
        if (!left) showEmpty(); else setCount(left);
      };
      if (reduceMotion) { done(); return; }
      li.classList.add('is-leaving');
      setTimeout(done, 280);
    });

    // Ändras listan i en annan flik: läs in på nytt
    window.addEventListener('storage', (e) => { if (e.key === 'vildblad:wishlist') load(); });
    load();
  }

  /* ==========================================================================
     Shoppa efter kategori: flikar med produkter
     ========================================================================== */
  function initCategoryTabs(section) {
    const tabs = $$('[data-cat-tab]', section);
    const panel = $('[data-cat-panel]', section);
    const track = $('[data-cat-track]', section);
    const all = $('[data-cat-all]', section);
    const allText = $('[data-cat-all-text]', section);
    if (!tabs.length || !panel || !track) return;
    const exclude = section.dataset.exclude || '';
    const cache = new Map();
    let current = tabs[0];
    let token = 0;

    const limit = parseInt(track.dataset.limit, 10) || 12;
    let failed = false;
    const clean = (html) => {
      const tmp = document.createElement('ul');
      tmp.innerHTML = html;
      if (exclude) $$(`[data-handle="${CSS.escape(exclude)}"]`, tmp).forEach((li) => li.remove());
      Array.from(tmp.children).slice(limit).forEach((li) => li.remove());
      return tmp.innerHTML;
    };

    // Hämtar en fliks produkter en gång. Misslyckas det försöker nästa klick igen.
    const rail = (url) => {
      if (!cache.has(url)) {
        cache.set(url, fetch(`${url}${url.includes('?') ? '&' : '?'}view=rail`, { credentials: 'same-origin' })
          .then((res) => (res.ok ? res.text() : Promise.reject(new Error(String(res.status)))))
          .then(clean)
          .catch(() => { cache.delete(url); return null; }));
      }
      return cache.get(url);
    };
    cache.set(tabs[0].dataset.url, Promise.resolve(track.innerHTML));

    async function select(tab, focus) {
      if (tab === current && !failed) return;
      tabs.forEach((t) => {
        const on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      if (focus) tab.focus();
      current = tab;
      panel.setAttribute('aria-labelledby', tab.id);
      if (all) all.href = tab.dataset.url;
      if (allText) allText.textContent = fill(S.catViewAll, { title: tab.dataset.title, count: tab.dataset.count });

      const my = ++token;
      const timer = setTimeout(() => { panel.classList.add('is-loading'); track.setAttribute('aria-busy', 'true'); }, 120);
      const html = await rail(tab.dataset.url);
      clearTimeout(timer);
      if (my !== token) return;
      panel.classList.remove('is-loading');
      track.removeAttribute('aria-busy');
      failed = html === null;
      if (html === null) track.innerHTML = `<li class="cat-panel__error">${S.catError || ''}</li>`;
      else if (!html.trim()) track.innerHTML = `<li class="cat-panel__error">${S.catEmpty || ''}</li>`;
      else track.innerHTML = html;
      track.scrollLeft = 0;
      track.dispatchEvent(new Event('scroll'));
      if (!reduceMotion) {
        panel.classList.remove('is-switching');
        void panel.offsetWidth;
        panel.classList.add('is-switching');
      }
    }

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(tab, false));
      tab.addEventListener('keydown', (e) => {
        let next = null;
        if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
        else if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (e.key === 'Home') next = tabs[0];
        else if (e.key === 'End') next = tabs[tabs.length - 1];
        if (!next) return;
        e.preventDefault();
        select(next, true);
      });
      // Förhämta när kunden pekar på fliken, så att bytet går snabbare
      tab.addEventListener('pointerenter', () => { rail(tab.dataset.url); }, { once: true });
    });
  }

  /* ==========================================================================
     Presentkort: skicka till mottagaren
     ========================================================================== */
  function initGiftRecipient(wrap) {
    const toggle = $('[data-gift-toggle]', wrap);
    const fields = $('[data-gift-fields]', wrap);
    if (!toggle || !fields) return;
    const inputs = $$('input, textarea', fields);
    const offset = $('[data-gift-offset]', wrap);
    const date = $('[data-gift-date]', wrap);
    const message = $('[data-gift-message]', wrap);
    const counter = $('[data-gift-count]', wrap);

    if (date) {
      const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
      const today = new Date();
      const max = new Date();
      max.setDate(max.getDate() + 90);
      date.min = iso(today);
      date.max = iso(max);
    }
    const update = () => {
      const on = toggle.checked;
      fields.hidden = !on;
      toggle.setAttribute('aria-expanded', on ? 'true' : 'false');
      inputs.forEach((el) => { el.disabled = !on; });
      if (offset) offset.value = String(new Date().getTimezoneOffset());
    };
    toggle.addEventListener('change', update);
    if (message && counter) {
      const max = parseInt(message.getAttribute('maxlength'), 10) || 200;
      message.addEventListener('input', () => { counter.textContent = fill(S.giftLeft, { count: max - message.value.length }); });
    }
    if (toggle.checked) update();

    // Formuläret har novalidate: kontrollera mottagarens e-post innan kortet läggs i varukorgen
    const form = wrap.closest('form');
    const email = $('input[type="email"]', fields);
    if (form && email) {
      document.addEventListener('submit', (e) => {
        if (e.target !== form || !toggle.checked) return;
        if (email.checkValidity() && email.value.trim()) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        email.reportValidity();
        email.focus();
      }, true);
    }
  }

  /* ==========================================================================
     Nedsatt pris: förklaringen följer vald variant
     ========================================================================== */
  document.addEventListener('variant:change', (e) => {
    const variant = e.detail && e.detail.variant;
    const section = e.target && e.target.closest ? e.target.closest('[data-product-section]') || e.target : document;
    const note = $('[data-lowest-note]', section);
    if (!note) return;
    note.hidden = !(variant && variant.compare_at_price > variant.price);
  });

  /* ---------- Start ---------- */
  function init(scope = document) {
    Wishlist.sync(scope);
    if (scope !== document) Wishlist.sync();
    $$('[data-wishlist]', scope).forEach(initWishlistPage);
    $$('[data-cat-explorer]', scope).forEach(initCategoryTabs);
    $$('[data-gift-recipient]', scope).forEach(initGiftRecipient);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  V.Wishlist = Wishlist;
})();
